import React, { useState, useEffect } from 'react';
import { Bell, Filter, AlertTriangle, Info, Download, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

import Header from '../../components/ui/Header';
import NotificationFeed from './components/NotificationFeed';
import PriorityFilterSidebar from './components/PriorityFilterSidebar';
import TrendChart from './components/TrendChart';
import AlertDetailModal from './components/AlertDetailModal';

// Sample data for preview
const SAMPLE_NOTIFICATIONS = [
  {
    id: '1',
    type: 'compliance',
    priority: 'critical',
    title: 'Chemical Application Limit Exceeded',
    message: 'Field Zone A has exceeded the maximum allowed pesticide application rate for this quarter. Immediate review required.',
    created_at: new Date(Date.now() - 30 * 60000)?.toISOString(),
    isAcknowledged: false
  },
  {
    id: '2',
    type: 'environmental',
    priority: 'high',
    title: 'Soil Moisture Anomaly Detected',
    message: 'AI system detected unusual soil moisture patterns in Field B. Potential irrigation system malfunction.',
    created_at: new Date(Date.now() - 2 * 3600000)?.toISOString(),
    isAcknowledged: false
  },
  {
    id: '3',
    type: 'audit',
    priority: 'medium',
    title: 'Treatment Plan Modified',
    message: 'User John Smith modified treatment plan TP-2024-045 for Field C. Changes logged for compliance review.',
    created_at: new Date(Date.now() - 5 * 3600000)?.toISOString(),
    isAcknowledged: true
  },
  {
    id: '4',
    type: 'compliance',
    priority: 'high',
    title: 'Safety Data Sheet Expiring Soon',
    message: 'SDS for product "BioGuard Plus" expires in 7 days. Update required to maintain compliance.',
    created_at: new Date(Date.now() - 8 * 3600000)?.toISOString(),
    isAcknowledged: false
  },
  {
    id: '5',
    type: 'environmental',
    priority: 'medium',
    title: 'Temperature Threshold Warning',
    message: 'Greenhouse Zone 3 temperature reached 32°C, exceeding optimal range. Ventilation system activated.',
    created_at: new Date(Date.now() - 12 * 3600000)?.toISOString(),
    isAcknowledged: true
  },
  {
    id: '6',
    type: 'audit',
    priority: 'low',
    title: 'Product Inventory Updated',
    message: 'Inventory count completed for warehouse location W-12. 45 items updated in system.',
    created_at: new Date(Date.now() - 24 * 3600000)?.toISOString(),
    isAcknowledged: true
  },
  {
    id: '7',
    type: 'compliance',
    priority: 'critical',
    title: 'Re-Entry Interval Violation Risk',
    message: 'Field D scheduled for harvesting within restricted re-entry period. Task requires immediate rescheduling.',
    created_at: new Date(Date.now() - 36 * 3600000)?.toISOString(),
    isAcknowledged: false
  },
  {
    id: '8',
    type: 'environmental',
    priority: 'low',
    title: 'Humidity Levels Normalized',
    message: 'Greenhouse Zone 1 humidity returned to optimal range (65-75%). No action required.',
    created_at: new Date(Date.now() - 48 * 3600000)?.toISOString(),
    isAcknowledged: true
  }
];

const SAMPLE_STATS = {
  total: 8,
  unacknowledged: 4,
  critical: 2,
  high: 2,
  medium: 2,
  low: 2,
  byType: {
    audit: 2,
    compliance: 3,
    environmental: 3
  }
};

const SAMPLE_TRENDS = [
  { date: new Date(Date.now() - 6 * 86400000)?.toISOString(), audit: 3, compliance: 5, environmental: 4, total: 12 },
  { date: new Date(Date.now() - 5 * 86400000)?.toISOString(), audit: 2, compliance: 3, environmental: 6, total: 11 },
  { date: new Date(Date.now() - 4 * 86400000)?.toISOString(), audit: 4, compliance: 4, environmental: 3, total: 11 },
  { date: new Date(Date.now() - 3 * 86400000)?.toISOString(), audit: 1, compliance: 6, environmental: 5, total: 12 },
  { date: new Date(Date.now() - 2 * 86400000)?.toISOString(), audit: 3, compliance: 2, environmental: 4, total: 9 },
  { date: new Date(Date.now() - 1 * 86400000)?.toISOString(), audit: 2, compliance: 4, environmental: 2, total: 8 },
  { date: new Date()?.toISOString(), audit: 2, compliance: 3, environmental: 3, total: 8 }
];

const UnifiedNotificationCenter = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState(SAMPLE_NOTIFICATIONS);
  const [stats, setStats] = useState(SAMPLE_STATS);
  const [trends, setTrends] = useState(SAMPLE_TRENDS);
  const [loading, setLoading] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [showTrends, setShowTrends] = useState(true);

  // Filter states
  const [filters, setFilters] = useState({
    severity: null,
    type: null,
    showAcknowledged: true
  });

  // Filter notifications based on active filters
  const filteredNotifications = notifications?.filter(notification => {
    if (filters?.severity && notification?.priority !== filters?.severity) return false;
    if (filters?.type && notification?.type !== filters?.type) return false;
    if (!filters?.showAcknowledged && notification?.isAcknowledged) return false;
    return true;
  });

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: filterType === 'showAcknowledged' ? value : (prev?.[filterType] === value ? null : value)
    }));
  };

  const handleAcknowledge = async (alert) => {
    // Update notification as acknowledged
    setNotifications(prev => 
      prev?.map(n => n?.id === alert?.id ? { ...n, isAcknowledged: true } : n)
    );
    
    // Update stats
    setStats(prev => ({
      ...prev,
      unacknowledged: prev?.unacknowledged - 1
    }));
  };

  const handleBulkAcknowledge = async () => {
    const alertsToAcknowledge = notifications?.filter(n => 
      selectedNotifications?.includes(n?.id) && !n?.isAcknowledged && n?.type !== 'audit'
    );
    
    if (alertsToAcknowledge?.length === 0) return;

    // Update all selected notifications as acknowledged
    setNotifications(prev => 
      prev?.map(n => 
        selectedNotifications?.includes(n?.id) && !n?.isAcknowledged && n?.type !== 'audit'
          ? { ...n, isAcknowledged: true }
          : n
      )
    );
    
    // Update stats
    setStats(prev => ({
      ...prev,
      unacknowledged: prev?.unacknowledged - alertsToAcknowledge?.length
    }));
    
    setSelectedNotifications([]);
  };

  const handleExport = async () => {
    const dataStr = JSON.stringify(filteredNotifications, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `notifications-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const handleViewDetails = async (alert) => {
    // Enhance alert with additional details for modal
    const enhancedAlert = {
      ...alert,
      action: alert?.type === 'audit' ? 'UPDATE' : undefined,
      category: alert?.type === 'audit' ? 'treatment_plan' : undefined,
      level: alert?.priority,
      description: alert?.message,
      alert_type: alert?.type,
      severity: alert?.priority
    };
    setSelectedAlert(enhancedAlert);
    setShowDetailModal(true);
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => 
      prev?.includes(notificationId) 
        ? prev?.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications?.length === filteredNotifications?.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications?.map(n => n?.id));
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': case 'high':
        return <AlertTriangle className="w-5 h-5" />;
      case 'medium':
        return <Bell className="w-5 h-5" />;
      case 'low':
        return <Info className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Notification Center</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Manage all system notifications</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Alerts</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.total}</p>
              </div>
              <Bell className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unacknowledged</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{stats?.unacknowledged}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Critical</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats?.critical}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Acknowledged</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats?.total - stats?.unacknowledged}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkAcknowledge}
                disabled={selectedNotifications?.length === 0}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                Acknowledge Selected ({selectedNotifications?.length})
              </button>
              
              <button
                onClick={() => setShowTrends(!showTrends)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                {showTrends ? 'Hide' : 'Show'} Trends
              </button>
            </div>

            <button
              onClick={handleExport}
              className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Trend Chart */}
        {showTrends && (
          <div className="mb-6">
            <TrendChart trends={trends} />
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Priority Filter Sidebar */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <PriorityFilterSidebar 
              filters={filters}
              onFilterChange={handleFilterChange}
              stats={stats}
            />
          </div>

          {/* Notification Feed */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <NotificationFeed
              notifications={filteredNotifications}
              loading={loading}
              selectedNotifications={selectedNotifications}
              onSelectNotification={handleSelectNotification}
              onSelectAll={handleSelectAll}
              onAcknowledge={handleAcknowledge}
              onViewDetails={handleViewDetails}
              getPriorityColor={getPriorityColor}
              getPriorityIcon={getPriorityIcon}
            />
          </div>
        </div>
      </div>
      {/* Alert Detail Modal */}
      {showDetailModal && selectedAlert && (
        <AlertDetailModal
          alert={selectedAlert}
          onClose={() => setShowDetailModal(false)}
          onAcknowledge={handleAcknowledge}
          getPriorityColor={getPriorityColor}
          getPriorityIcon={getPriorityIcon}
        />
      )}
    </div>
  );
};

export default UnifiedNotificationCenter;